import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Package, CaretDown, CaretUp, ShieldCheck, Warning } from "@phosphor-icons/react";

/**
 * MaterialsTraceabilityPanel — case-level material trace surface for the
 * doctor's case detail page. Lists every batch (lot number, supplier,
 * received/expiry dates, manufacturer) consumed for this case so the doctor
 * can answer "which exact sheet did this patient get?"
 *
 * Endpoint: GET /api/cases/{id}/materials-traceability
 */
export function MaterialsTraceabilityPanel({ caseId }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!caseId) return;
        api.get(`/cases/${caseId}/materials-traceability`)
            .then((r) => setData(r.data))
            .catch(() => setData({ batches: [] }));
    }, [caseId]);

    if (!data) return null;
    const batches = data.batches || [];
    if (batches.length === 0) return null;

    const hasRecall = batches.some((b) => b.status === "recalled");

    return (
        <div
            className={`rounded-2xl border bg-card overflow-hidden ${hasRecall ? "border-rose-200" : "border-border"}`}
            data-testid="materials-traceability-panel"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/30 transition"
                data-testid="materials-trace-toggle"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${hasRecall ? "bg-rose-100" : "bg-emerald-100"}`}>
                        {hasRecall ? <Warning size={18} weight="fill" className="text-rose-700" /> : <ShieldCheck size={18} weight="fill" className="text-emerald-700" />}
                    </div>
                    <div className="text-left min-w-0">
                        <div className="font-display font-bold text-sm sm:text-base">
                            Materials used · {batches.length} batch{batches.length === 1 ? "" : "es"} traced
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {hasRecall
                                ? "⚠ One or more lots used on this case have since been recalled."
                                : "Every sheet thermoformed for this patient is traceable back to its lot."}
                        </div>
                    </div>
                </div>
                {open ? <CaretUp size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
            </button>

            {open && (
                <div className="border-t border-border p-5 space-y-3">
                    {batches.map((b, i) => (
                        <div
                            key={b.id || i}
                            className={`rounded-xl border p-3.5 ${b.status === "recalled" ? "border-rose-200 bg-rose-50/40" : "border-border bg-muted/20"}`}
                            data-testid={`materials-trace-batch-${i}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Package size={15} weight="fill" className="text-primary" />
                                </div>
                                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <div className="md:col-span-2">
                                        <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mr-2">{b.batch_code}</span>
                                        <span className="font-semibold">{b.material_sku || "—"}</span>
                                        <span className="text-muted-foreground"> — {b.material_name || "—"}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Lot:</span>{" "}
                                        <span className="font-mono font-semibold">{b.lot_number || "—"}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Supplier:</span>{" "}
                                        <span className="font-medium">{b.supplier || "—"}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Manufactured:</span>{" "}
                                        <span className="font-medium">{b.manufacture_date || "—"}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Expires:</span>{" "}
                                        <span className="font-medium">{b.expiry_date || "—"}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Sheets used:</span>{" "}
                                        <span className="font-bold tabular-nums">{b.total_consumed}</span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Status:</span>{" "}
                                        <span className={`inline-block text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${b.status === "recalled" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
                                            {b.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default MaterialsTraceabilityPanel;
